// reducers
import {
  connectRouter,
  LocationChangeAction,
  RouterState,
} from 'connected-react-router'
import { History, Location } from 'history'
import { Reducer } from 'redux'
import { ExtendLocales, OrganizationRoles } from 'src/enums'
import { OrgExpenseReportStatus } from 'src/models/orgExpenseReports.models'
import {
  TransactionDateType,
  TransactionExpenseCategoryStatus,
  TransactionReceiptStatus,
  TransactionSource,
} from 'src/models/transaction.models'
import AppReducer, {
  Actions as AppActions,
  AppState,
  initialState as appInitialState,
} from 'src/redux/reducers/app.reducer'
import AuthReducer, {
  Actions as AuthActions,
  AuthState,
  initialState as authInitialState,
} from 'src/redux/reducers/auth.reducer'
import DataReducer, {
  Actions as DataActions,
  DataState,
  initialState as dataInitialState,
} from 'src/redux/reducers/data.reducer'
import SessionReducer, {
  Actions as SessionActions,
  initialState as sessionInitialState,
  SessionState,
} from 'src/redux/reducers/session.reducer'

export interface RootState {
  router: RouterState<LocationState>
  data: DataState
  auth: AuthState
  app: AppState
  session: SessionState
}

export interface LocationState {
  backgroundLocation?: Location<LocationState>
  autofill?: string
  password?: string
  acceptOrgInvite?: boolean
  screen?: string
  email?: string
  accountBinInputPageCvcFlatFields?: Record<string, string>
  accountBinInputPageFlatFields?: Record<string, string>
  paymentNetInfoFlatFields?: Record<string, string>
  currentPage?: number
  prevUrl?: string
  filters?: string
}

// TODO: how many of these are repeated? can we consolidate and map to distinct types?
export interface SearchQueryParams {
  type?: string
  search?: string
  user?: string[] | string
  users?: string[] | string
  userIds?: string[] | string
  userId?: string
  owners?: string[]
  receiptRequired?: string
  dateType?: TransactionDateType
  creditCardId?: string[] | string
  statuses?: string[] | string
  displayStatuses?: string[] | string
  status?: string
  required?: string[] | string
  requiredForTransactions?: string[] | string
  requiredForVirtualCards?: string[] | string
  source?: TransactionSource
  sinceClearedAt?: string
  since?: string
  sinceTimestamp?: string
  untilClearedAt?: string
  until?: string
  untilTimestamp?: string
  mccGroup?: string[] | string
  departments?: string[] | string
  recipient?: string
  recipients?: string[] | string
  recipientOrPhysicalCardUserId?: string[] | string
  cardholders?: string[] | string
  receiptStatus?: TransactionReceiptStatus[] | TransactionReceiptStatus
  reviewStatus?: OrgExpenseReportStatus[] | OrgExpenseReportStatus
  receiptMissing?: string
  hasAttachment?: string
  sort?: string
  orgName?: string
  organizationRole?: OrganizationRoles
  inviteeEmail?: string
  inviterName?: string
  inviterEmail?: string
  // Bulk-invite signup deep link prefills these (email stays locked).
  firstName?: string
  lastName?: string
  phone?: string
  // Deep-links the Bulk Invite (CSV) modal open over /people/members.
  bulkInvite?: string
  cardTypes?: string[]
  deactivatingIn?: string
  organizationRoles?: OrganizationRoles[]
  showDeactivatedUsers?: string
  expenseCategoryStatuses?:
    | TransactionExpenseCategoryStatus[]
    | TransactionExpenseCategoryStatus
  expenseCategories?: string // this value must be a stringified Record<string, string[]> to track expCats and their labels
  syncStatuses?: string[]
  minClearingBillingCents?: number
  maxClearingBillingCents?: number
  locale?: ExtendLocales
  orgExpenseReportId?: string
  active?: string
  expensesView?: 'approver' | 'manager' | 'submitter'
}

type ControlledActions = DataActions | AuthActions | AppActions | SessionActions

export const initialRootState: RootState = {
  router: {
    location: {
      pathname: '/',
      search: '',
      hash: '',
      state: {},
      key: '',
      query: {},
    },
    action: 'REPLACE',
  },
  app: appInitialState,
  auth: authInitialState,
  data: dataInitialState,
  session: sessionInitialState,
}

export type Actions = LocationChangeAction | ControlledActions

const createRootReducer =
  (history: History<LocationState>): Reducer<RootState, Actions> =>
  (state: RootState | undefined, action: Actions): RootState => ({
    router: connectRouter(history)(
      state?.router,
      action as LocationChangeAction,
    ),
    data: DataReducer(state?.data, action as DataActions),
    auth: AuthReducer(state?.auth, action as AuthActions),
    app: AppReducer(state?.app, action as AppActions),
    session: SessionReducer(state?.session, action as SessionActions),
  })

export default createRootReducer
